import { useState, useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDb,
  selectListAllVehiclesInDbStatus,
} from "../vehicles/vehiclesSlice";
import { selectCompany } from "../homeSlice";
import { useNavigate } from "react-router-dom";
import { LaunchPage, getDriverUsageAtDateByVehicle } from "@qivia/ui";
import { fakeVehicleGeolocatedForDemo, fakeTripsByDayListForDemo } from "./lib";
import {
  getLastDataPointsByCompanyAndDateAsync,
  selectLastDataPointsVehicles,
  selectLastDataPointsVehiclesStatus,
  selectDataPointsByVehicleStatus,
} from "./geolocationSlice";
import {
  advancedAnalyticVehiclesAsync,
  driversHistoryAsync,
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
  selectDriversHistoryList,
  selectDriversHistoryListStatus,
} from "../fleet/fleetSlice";
import {
  tripsAsync,
  selectTripsByDayList,
  selectTripsByDayStatus,
} from "../fleet/trips/tripsSlice";
import { DataPoint } from "./geolocationApi";
import { SharedInterfaceGeolocation } from "./sharedInterface";

export type VehicleGeolocated = {
  uuid: string;
  registrationNumber: string;
  model?: string;
  reference: string;
  lastLatitude: number;
  lastLongitude: number;
  distance: number;
  driverName: string | null;
  driverPhone: string | null;
  dataPoints: DataPoint[];
};

export const Geolocation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const vehiclesList = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const lastDataPointsVehiclesStatus = useAppSelector(
    selectLastDataPointsVehiclesStatus,
  );
  const dataPointsByVehicleStatus = useAppSelector(
    selectDataPointsByVehicleStatus,
  );
  const lastDataPointsVehicles = useAppSelector(selectLastDataPointsVehicles);
  const advancedAnalyticsVehicleList = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const tripsByDayList = useAppSelector(selectTripsByDayList);
  const tripsByDayStatus = useAppSelector(selectTripsByDayStatus);
  const driversHistoryList = useAppSelector(selectDriversHistoryList);
  const driversHistoryListStatus = useAppSelector(
    selectDriversHistoryListStatus,
  );
  const [date, setDate] = useState<Date>(new Date());

  const isNotProductionEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] !== "production";
  }, []);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(
        listAllVehiclesInDbAsync({ companyUuid: company?.uuid || "" }),
      );
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && advancedAnalyticsVehicleListStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesAsync(company.uuid));
    }
  }, [company, dispatch, advancedAnalyticsVehicleListStatus]);

  useEffect(() => {
    if (company && driversHistoryListStatus === "idle") {
      void dispatch(driversHistoryAsync(company.uuid));
    }
  }, [company, dispatch, driversHistoryListStatus]);

  useEffect(() => {
    if (
      company &&
      advancedAnalyticsVehicleList.length > 0 &&
      advancedAnalyticsVehicleListStatus === "success" &&
      tripsByDayStatus === "idle"
    ) {
      void dispatch(
        tripsAsync({
          vehicles: advancedAnalyticsVehicleList,
          from: date.toISOString(),
          to: date.toISOString(),
        }),
      );
    }
  }, [
    company,
    dispatch,
    advancedAnalyticsVehicleList,
    advancedAnalyticsVehicleListStatus,
    tripsByDayStatus,
    date,
  ]);

  useEffect(() => {
    if (company && lastDataPointsVehiclesStatus === "idle") {
      void dispatch(
        getLastDataPointsByCompanyAndDateAsync({
          companyUuid: company.uuid,
          date: date.toISOString(),
        }),
      );
    }
  }, [company, date, dispatch, lastDataPointsVehiclesStatus]);

  const driverAtDateByVehicle = useMemo(
    () => getDriverUsageAtDateByVehicle(driversHistoryList, date),
    [driversHistoryList, date],
  );

  const lastDataPointsVehiclesToDisplayed = useMemo<VehicleGeolocated[]>(() => {
    if (!lastDataPointsVehicles) return [];
    return lastDataPointsVehicles.reduce((acc, vehicle) => {
      const vehicleData = vehiclesList.find(
        (v) => v.uuid === vehicle.vehicleUuid,
      );
      if (!vehicleData) return acc;
      const driverAtDate = driverAtDateByVehicle[vehicle.vehicleUuid];
      const driverName = driverAtDate
        ? `${driverAtDate.driverInfo.firstName} ${driverAtDate.driverInfo.lastName}`
        : null;
      const isDriverAtDateStillOnTheVehicle =
        vehicleData.driverUuid === driverAtDate?.driverUuid;
      const driverPhone = isDriverAtDateStillOnTheVehicle
        ? vehicleData.driverPhone
        : null;
      acc.push({
        uuid: vehicleData.uuid,
        registrationNumber: vehicleData.registrationNumber,
        model: vehicleData.model,
        reference: vehicleData.reference,
        lastLatitude: vehicle.lat,
        lastLongitude: vehicle.lon,
        dataPoints: vehicle.dataPoints,
        distance: 0,
        driverName,
        driverPhone,
      });
      return acc;
    }, [] as VehicleGeolocated[]);
  }, [lastDataPointsVehicles, vehiclesList, driverAtDateByVehicle]);

  const handleDateChange = useCallback(
    (date: Date) => {
      if (!company) return;
      setDate(date);
      void dispatch(
        getLastDataPointsByCompanyAndDateAsync({
          companyUuid: company.uuid,
          date: date.toISOString(),
        }),
      );
      void dispatch(
        tripsAsync({
          vehicles: advancedAnalyticsVehicleList,
          from: date.toISOString(),
          to: date.toISOString(),
        }),
      );
    },
    [dispatch, company, advancedAnalyticsVehicleList],
  );

  if (company && !company.hasGeolocation && !isNotProductionEnvironment()) {
    navigate("/home");
  }

  if (lastDataPointsVehiclesStatus === "processing") {
    return <LaunchPage hasBorderRadius />;
  }

  if (!company) {
    return;
  }

  return (
    <SharedInterfaceGeolocation
      lastDataPointsVehiclesToDisplayed={lastDataPointsVehiclesToDisplayed}
      fakeVehicleGeolocatedForDemo={fakeVehicleGeolocatedForDemo}
      isNotProductionEnvironment={isNotProductionEnvironment}
      date={date}
      onDateChange={handleDateChange}
      tripsByDayList={tripsByDayList}
      fakeTripsByDayListForDemo={fakeTripsByDayListForDemo}
      isDataPointsLoading={dataPointsByVehicleStatus === "processing"}
    />
  );
};
